import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Toaster } from "@/components/ui/sonner";
import { useAnalyticsTracker } from "@/lib/analytics";
import { useWebVitalsBeacon } from "@/lib/web-vitals-beacon";
import { useGa4PageViews, useGa4ClickToCall } from "@/lib/ga4";
import logoAsset from "@/assets/homestead-logo.png.asset.json";

const SITE_URL = "https://homesteadconstruction.us";
const FAVICON_URL = `${SITE_URL}${logoAsset.url}`;
const OG_IMAGE_URL = FAVICON_URL;
const LOGO_URL = FAVICON_URL;

const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  name: "Homestead Construction of NEA",
  alternateName: "Homestead Construction",
  url: SITE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  telephone: "+1-501-593-6361",
  priceRange: "$$",
  founder: {
    "@type": "Person",
    name: "Wesley Rhodes",
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Searcy",
    addressRegion: "AR",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "City", name: "Searcy" },
    { "@type": "AdministrativeArea", name: "Northeast Arkansas" },
    { "@type": "State", name: "Arkansas" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "17:00",
    },
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home Remodeling" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Room Additions" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Basement Finishing" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Garage Conversions" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Kitchen Remodeling" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Bathroom Remodeling" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Decks and Patios" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Custom Builds" } },
  ],
  slogan: "Your Vision. Our Craft.",
  identifier: "License #0450850225",
};

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Homestead Construction of NEA",
  alternateName: "Homestead Construction",
  url: SITE_URL,
  logo: LOGO_URL,
  founder: { "@type": "Person", name: "Wesley Rhodes" },
};

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Homestead Construction" },
      { property: "og:site_name", content: "Homestead Construction" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "1200" },
      { property: "og:logo", content: LOGO_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE_URL },
      { name: "theme-color", content: "#E8551F" },
      { name: "geo.region", content: "US-AR" },
      { name: "geo.placename", content: "Searcy, Arkansas" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: logoAsset.url },
      { rel: "shortcut icon", type: "image/png", href: logoAsset.url },
      { rel: "apple-touch-icon", href: logoAsset.url },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://hdqeqzsmidmgljoowbvp.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://hdqeqzsmidmgljoowbvp.supabase.co" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Mulish:wght@400;600;700&display=swap",
        media: "print",
      },
    ],

    scripts: [
      {
        children:
          "(function(){function s(){document.querySelectorAll('link[rel=\"stylesheet\"][media=\"print\"]').forEach(function(l){l.media='all';});}if(document.readyState!=='loading'){s();}else{document.addEventListener('DOMContentLoaded',s);}})();",
      },
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-S574RV39L0",
        async: true,
      },

      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js', new Date());gtag('config', 'G-S574RV39L0', { send_page_view: false });",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify(localBusinessJsonLd),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(organizationJsonLd),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// Defers non-critical instrumentation until the main thread is idle so it can't
// contribute to long tasks during first paint.
function useIdleReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    if (w.requestIdleCallback) {
      w.requestIdleCallback(() => setReady(true), { timeout: 3000 });
      return;
    }
    const t = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(t);
  }, []);
  return ready;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideChrome =
    pathname === "/auth" ||
    pathname.startsWith("/auth/") ||
    pathname === "/dashboard" ||
    pathname.startsWith("/dashboard/");

  const idle = useIdleReady();
  useAnalyticsTracker(hideChrome || !idle);
  useWebVitalsBeacon(hideChrome || !idle);
  useGa4PageViews();
  useGa4ClickToCall();


  return (
    <QueryClientProvider client={queryClient}>
      {hideChrome ? (
        <Outlet />
      ) : (
        <div className="flex min-h-screen flex-col">
          <Header />
          <main className="flex-1">
            <Outlet />
          </main>
          <Footer />
        </div>
      )}
      <Toaster />
    </QueryClientProvider>
  );
}
